import React, { useState, useEffect, useCallback } from 'react';
import axios from './lib/axiosClient';

var AUDIENCES = [
  { id: 'all', label: 'All Customers', desc: 'Everyone with a phone number', icon: '👥' },
  { id: 'recent_30d', label: 'Recent (30 days)', desc: 'Visited in the last 30 days', icon: '🔥' },
  { id: 'inactive_90d', label: 'Inactive (90+ days)', desc: 'Haven\'t visited in 90+ days', icon: '💤' },
  { id: 'vip', label: 'VIP ($500+ spent)', desc: 'High-value customers', icon: '⭐' },
];

var TEMPLATES = [
  { name: 'Promo', msg: 'Hi {first_name}! 🎉 We have a special offer just for you. Book today and save! Reply STOP to opt out.' },
  { name: 'Reminder', msg: 'Hi {first_name}, we miss you! It\'s been a while since your last visit. Book now and let us take care of you! Reply STOP to opt out.' },
  { name: 'Holiday', msg: 'Happy holidays from our team! 🎄 Treat yourself or someone special with a gift card. Reply STOP to opt out.' },
  { name: 'New Service', msg: 'Hi {first_name}! We just added new services. Check them out and book your next visit! Reply STOP to opt out.' },
];

function SMSCampaigns() {
  var [campaigns, setCampaigns] = useState([]);
  var [loading, setLoading] = useState(true);
  var [view, setView] = useState('list');
  var [activeTab, setActiveTab] = useState('campaigns');
  var [editCampaign, setEditCampaign] = useState(null);

  // Campaign form
  var [name, setName] = useState('');
  var [message, setMessage] = useState('');
  var [audience, setAudience] = useState('all');
  var [audienceCount, setAudienceCount] = useState(0);
  var [audiencePreview, setAudiencePreview] = useState([]);
  var [excluded, setExcluded] = useState([]);
  var [showPreview, setShowPreview] = useState(false);
  var [saving, setSaving] = useState(false);
  var [sending, setSending] = useState(false);
  var [flash, setFlash] = useState('');

  // Individual send
  var [indMessage, setIndMessage] = useState('');
  var [indSearch, setIndSearch] = useState('');
  var [indSearchResults, setIndSearchResults] = useState([]);
  var [indSelected, setIndSelected] = useState([]);
  var [indSending, setIndSending] = useState(false);
  var [indFlash, setIndFlash] = useState(null);
  var [indSearchLoading, setIndSearchLoading] = useState(false);
  var [manualPhone, setManualPhone] = useState('');
  var [manualName, setManualName] = useState('');

  var loadCampaigns = useCallback(async function() {
    try {
      var res = await axios.get('/api/sms-campaigns');
      if (res.data?.success) setCampaigns(res.data.campaigns || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(function() { loadCampaigns(); }, [loadCampaigns]);

  var loadAudienceCount = useCallback(async function(aud) {
    try {
      var res = await axios.get('/api/sms-campaigns/audience/preview?audience=' + aud + '&limit=100');
      if (res.data?.success) {
        setAudienceCount(res.data.count);
        setAudiencePreview(res.data.customers || []);
      }
    } catch (e) { setAudienceCount(0); setAudiencePreview([]); }
  }, []);

  useEffect(function() {
    if (view === 'compose') { setExcluded([]); loadAudienceCount(audience); }
  }, [audience, view, loadAudienceCount]);

  // Search contacts
  useEffect(function() {
    if (!indSearch.trim()) { setIndSearchResults([]); return; }
    var timer = setTimeout(async function() {
      setIndSearchLoading(true);
      try {
        var res = await axios.get('/api/contacts?search=' + encodeURIComponent(indSearch) + '&limit=20');
        var contacts = res.data?.contacts || res.data?.data || res.data || [];
        if (Array.isArray(contacts)) setIndSearchResults(contacts.filter(function(c) { return c.phone; }));
      } catch (e) { setIndSearchResults([]); }
      setIndSearchLoading(false);
    }, 300);
    return function() { clearTimeout(timer); };
  }, [indSearch]);

  var showFlash = function(msg) { setFlash(msg); setTimeout(function() { setFlash(''); }, 3000); };
  var showIndFlash = function(msg, isError) { setIndFlash({ text: msg, error: isError }); setTimeout(function() { setIndFlash(null); }, 4000); };

  var addToSelected = function(contact) {
    if (indSelected.find(function(c) { return c.id === contact.id; })) return;
    var displayName = ((contact.first_name || '') + ' ' + (contact.last_name || '')).trim() || contact.name || 'Unknown';
    setIndSelected(function(prev) { return [...prev, { id: contact.id, name: displayName, phone: contact.phone }]; });
    setIndSearch('');
    setIndSearchResults([]);
  };

  var addManualPhone = function() {
    if (!manualPhone.trim()) return;
    if (indSelected.find(function(c) { return c.phone === manualPhone.trim(); })) { showIndFlash('That number is already added', true); return; }
    setIndSelected(function(prev) { return [...prev, { id: 'manual_' + Date.now(), name: manualName.trim() || manualPhone.trim(), phone: manualPhone.trim() }]; });
    setManualPhone(''); setManualName('');
  };

  var removeFromSelected = function(id) { setIndSelected(function(prev) { return prev.filter(function(c) { return c.id !== id; }); }); };

  var sendIndividual = async function() {
    if (!indMessage.trim()) { showIndFlash('Please enter a message', true); return; }
    if (indSelected.length === 0) { showIndFlash('Add at least one recipient', true); return; }
    if (!window.confirm('Send to ' + indSelected.length + ' recipient' + (indSelected.length > 1 ? 's' : '') + '?')) return;
    setIndSending(true);
    var results = { sent: 0, failed: 0 };
    for (var i = 0; i < indSelected.length; i++) {
      var recipient = indSelected[i];
      try {
        var personalizedMsg = indMessage.replace(/\{first_name\}/gi, recipient.name.split(' ')[0] || recipient.name);
        await axios.post('/api/sms/send', { phone: recipient.phone, message: personalizedMsg });
        results.sent++;
      } catch (e) { results.failed++; }
    }
    setIndSending(false);
    if (results.failed === 0) {
      showIndFlash('✓ Sent to ' + results.sent + ' recipient' + (results.sent > 1 ? 's' : '') + '!', false);
      setIndMessage(''); setIndSelected([]);
    } else {
      showIndFlash(results.sent + ' sent, ' + results.failed + ' failed', results.sent === 0);
    }
  };

  var openCompose = function(campaign) {
    if (campaign) { setEditCampaign(campaign); setName(campaign.name); setMessage(campaign.message); setAudience(campaign.audience || 'all'); }
    else { setEditCampaign(null); setName(''); setMessage(''); setAudience('all'); }
    setView('compose');
  };

  var saveDraft = async function() {
    if (!name.trim() || !message.trim()) return showFlash('Name and message required');
    if (message.length > 160) return showFlash('Message must be 160 chars or less');
    setSaving(true);
    try {
      if (editCampaign) await axios.put('/api/sms-campaigns/' + editCampaign.id, { name, message, audience });
      else await axios.post('/api/sms-campaigns', { name, message, audience });
      showFlash('Campaign saved!'); setView('list'); loadCampaigns();
    } catch (e) { showFlash('Error: ' + (e.response?.data?.error || e.message)); }
    setSaving(false);
  };

  var sendCampaign = async function(id) {
    if (!window.confirm('Send this campaign now? This cannot be undone.')) return;
    setSending(true);
    try {
      var res = await axios.post('/api/sms-campaigns/' + id + '/send');
      showFlash(res.data?.message || 'Sending!');
      setTimeout(loadCampaigns, 2000);
    } catch (e) { showFlash('Error: ' + (e.response?.data?.error || e.message)); }
    setSending(false); setView('list');
  };

  var deleteCampaign = async function(id) {
    if (!window.confirm('Delete this campaign?')) return;
    try { await axios.delete('/api/sms-campaigns/' + id); loadCampaigns(); showFlash('Deleted'); }
    catch (e) { showFlash('Error deleting'); }
  };

  var statusBadge = function(s) {
    var colors = { draft: ['#888', '#88881520'], scheduled: ['#ffab40', '#ffab4015'], sending: ['#0984e3', '#0984e315'], sent: ['#00b894', '#00b89415'], failed: ['#e74c3c', '#e74c3c15'] };
    var c = colors[s] || colors.draft;
    return { display: 'inline-block', padding: '4px 12px', borderRadius: 8, fontSize: 12, fontWeight: 700, color: c[0], background: c[1], textTransform: 'capitalize' };
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#888' }}>Loading...</div>;

  // COMPOSE VIEW
  if (view === 'compose') {
    return (
      <div style={{ padding: '0 0 40px' }}>
        {flash && <div style={{ background: '#0d2818', border: '1px solid #1a4d2e', borderRadius: 10, padding: '10px 16px', marginBottom: 16, color: '#34d399', fontSize: 13, fontWeight: 600 }}>{flash}</div>}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <div>
            <button onClick={function() { setView('list'); }} style={{ background: 'none', border: 'none', color: '#ff6b2b', fontSize: 13, fontWeight: 600, cursor: 'pointer', padding: 0, marginBottom: 8 }}>← Back to Campaigns</button>
            <h1 style={{ fontSize: 22, fontWeight: 800, color: '#fff', margin: 0 }}>{editCampaign ? 'Edit Campaign' : 'New Campaign'}</h1>
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: 20 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <div style={{ background: '#0d0d1f', borderRadius: 14, padding: 24, border: '1px solid #1e1e35' }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: '#888', marginBottom: 8 }}>Campaign Name</label>
              <input value={name} onChange={function(e) { setName(e.target.value); }} placeholder="e.g. Valentine's Day Promo"
                style={{ width: '100%', padding: '12px 14px', background: '#12121f', border: '1px solid #2a2a4a', borderRadius: 10, color: '#fff', fontSize: 14, outline: 'none', boxSizing: 'border-box' }} />
            </div>
            <div style={{ background: '#0d0d1f', borderRadius: 14, padding: 24, border: '1px solid #1e1e35' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <label style={{ fontSize: 13, fontWeight: 700, color: '#888' }}>Message</label>
                <span style={{ fontSize: 12, fontWeight: 600, color: message.length > 160 ? '#e74c3c' : message.length > 140 ? '#ffab40' : '#555' }}>{message.length}/160</span>
              </div>
              <textarea value={message} onChange={function(e) { setMessage(e.target.value); }} rows={4} placeholder="Type your message... Use {first_name} for personalization"
                style={{ width: '100%', padding: '12px 14px', background: '#12121f', border: '1px solid #2a2a4a', borderRadius: 10, color: '#fff', fontSize: 14, outline: 'none', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit', lineHeight: 1.5 }} />
              <div style={{ marginTop: 12, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 11, color: '#555', lineHeight: '24px' }}>Templates:</span>
                {TEMPLATES.map(function(t, i) {
                  return <button key={i} onClick={function() { setMessage(t.msg); }} style={{ padding: '4px 10px', background: '#1a1a30', border: '1px solid #2a2a4a', borderRadius: 6, color: '#888', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>{t.name}</button>;
                })}
              </div>
              <div style={{ marginTop: 8, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 11, color: '#555', lineHeight: '24px' }}>Insert:</span>
                {['{first_name}', '{last_name}'].map(function(v) {
                  return <button key={v} onClick={function() { setMessage(message + ' ' + v); }} style={{ padding: '4px 8px', background: '#ff6b2b10', border: '1px solid #ff6b2b30', borderRadius: 6, color: '#ff6b2b', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>{v}</button>;
                })}
              </div>
            </div>
            <div style={{ background: '#0d0d1f', borderRadius: 14, padding: 24, border: '1px solid #1e1e35' }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: '#888', marginBottom: 12 }}>Audience</label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                {AUDIENCES.map(function(a) {
                  var active = audience === a.id;
                  return (
                    <button key={a.id} onClick={function() { setAudience(a.id); }} style={{ padding: '14px 16px', background: active ? '#ff6b2b10' : '#12121f', border: '1px solid ' + (active ? '#ff6b2b' : '#2a2a4a'), borderRadius: 10, cursor: 'pointer', textAlign: 'left' }}>
                      <div style={{ fontSize: 20, marginBottom: 4 }}>{a.icon}</div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: active ? '#ff6b2b' : '#ccc' }}>{a.label}</div>
                      <div style={{ fontSize: 11, color: '#666', marginTop: 2 }}>{a.desc}</div>
                    </button>
                  );
                })}
              </div>
              <div style={{ marginTop: 14, background: '#12121f', borderRadius: 10, border: '1px solid #1e1e35', overflow: 'hidden' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', borderBottom: '1px solid #1e1e35' }}>
                  <div>
                    <span style={{ fontSize: 13, color: '#888' }}>Recipients: </span>
                    <span style={{ fontSize: 15, fontWeight: 800, color: '#fff' }}>{audienceCount - excluded.length}</span>
                    {excluded.length > 0 && <span style={{ fontSize: 11, color: '#e74c3c', marginLeft: 6 }}>({excluded.length} excluded)</span>}
                    <span style={{ fontSize: 12, color: '#555' }}> with phone numbers</span>
                  </div>
                  <button onClick={function() { setShowPreview(!showPreview); }} style={{ background: 'none', border: 'none', color: '#ff6b2b', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                    {showPreview ? '▲ Hide' : '▼ Preview'}
                  </button>
                </div>
                {showPreview && (
                  <div style={{ maxHeight: 220, overflowY: 'auto' }}>
                    {audiencePreview.length === 0 && <div style={{ padding: 16, color: '#555', fontSize: 13, textAlign: 'center' }}>No customers match this audience</div>}
                    {audiencePreview.map(function(c) {
                      var isExcluded = excluded.includes(c.id);
                      var initials = ((c.first_name||'')[0]||'') + ((c.last_name||'')[0]||'');
                      return (
                        <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 14px', borderBottom: '1px solid #252840', opacity: isExcluded ? 0.4 : 1 }}>
                          <div style={{ width: 30, height: 30, borderRadius: '50%', background: isExcluded ? '#333' : '#ff6b2b30', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: isExcluded ? '#555' : '#ff6b2b', flexShrink: 0 }}>{initials.toUpperCase() || '?'}</div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 13, fontWeight: 600, color: isExcluded ? '#555' : '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.first_name || ''} {c.last_name || ''}</div>
                            <div style={{ fontSize: 11, color: '#555' }}>{c.phone}</div>
                          </div>
                          {c.is_vip && <span style={{ fontSize: 10, background: '#ffab4020', color: '#ffab40', borderRadius: 5, padding: '2px 6px', fontWeight: 700 }}>VIP</span>}
                          <button onClick={function() { setExcluded(function(prev) { return isExcluded ? prev.filter(function(id) { return id !== c.id; }) : [...prev, c.id]; }); }}
                            style={{ padding: '3px 8px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 11, fontWeight: 700, background: isExcluded ? '#1a4d2e' : '#3d0000', color: isExcluded ? '#34d399' : '#e74c3c' }}>
                            {isExcluded ? '+ Add' : '✕ Remove'}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={saveDraft} disabled={saving} style={{ padding: '14px 28px', background: '#1e1e35', color: '#ccc', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: 'pointer', opacity: saving ? 0.6 : 1 }}>
                {saving ? 'Saving...' : 'Save Draft'}
              </button>
              {editCampaign && (editCampaign.status === 'draft' || editCampaign.status === 'scheduled') && (
                <button onClick={function() { sendCampaign(editCampaign.id); }} disabled={sending || audienceCount === 0}
                  style={{ padding: '14px 28px', background: 'linear-gradient(135deg, #ff6b2b, #ffab40)', color: '#fff', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: 'pointer', opacity: (sending || audienceCount === 0) ? 0.6 : 1 }}>
                  {sending ? 'Sending...' : 'Send Now (' + audienceCount + ')'}
                </button>
              )}
            </div>
          </div>
          <div>
            <div style={{ background: '#0d0d1f', borderRadius: 14, padding: 24, border: '1px solid #1e1e35', position: 'sticky', top: 20 }}>
              <h3 style={{ fontSize: 14, fontWeight: 700, color: '#888', marginBottom: 16, textTransform: 'uppercase', letterSpacing: 0.5 }}>📱 Preview</h3>
              <div style={{ background: '#1a1a30', borderRadius: 16, padding: 16, minHeight: 120 }}>
                <div style={{ background: '#2a2a4a', borderRadius: 12, padding: '12px 14px', maxWidth: '85%', fontSize: 14, color: '#e2e2e8', lineHeight: 1.5, wordBreak: 'break-word' }}>
                  {message ? message.replace(/\{first_name\}/gi, 'Sarah').replace(/\{last_name\}/gi, 'Johnson').replace(/\{name\}/gi, 'Sarah') : 'Your message preview will appear here...'}
                </div>
                {message && <div style={{ marginTop: 8, fontSize: 11, color: '#555' }}>{message.length <= 160 ? '1 segment' : Math.ceil(message.length / 153) + ' segments'} · {message.length} chars</div>}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // LIST VIEW
  var drafts = campaigns.filter(function(c) { return c.status === 'draft' || c.status === 'scheduled'; });
  var sent = campaigns.filter(function(c) { return c.status === 'sent' || c.status === 'sending' || c.status === 'failed'; });

  return (
    <div style={{ padding: '0 0 40px' }}>
      {flash && <div style={{ background: '#0d2818', border: '1px solid #1a4d2e', borderRadius: 10, padding: '10px 16px', marginBottom: 16, color: '#34d399', fontSize: 13, fontWeight: 600 }}>{flash}</div>}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: '#fff', margin: '0 0 4px' }}>📣 SMS</h1>
          <p style={{ fontSize: 14, color: '#888', margin: 0 }}>Send messages to individuals or run bulk campaigns</p>
        </div>
        {activeTab === 'campaigns' && (
          <button onClick={function() { openCompose(null); }} style={{ padding: '12px 24px', background: 'linear-gradient(135deg, #ff6b2b, #ffab40)', color: '#fff', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
            + New Campaign
          </button>
        )}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 28, background: '#0d0d1f', borderRadius: 12, padding: 4, border: '1px solid #1e1e35', width: 'fit-content' }}>
        {[{ id: 'campaigns', label: '📣 Campaigns' }, { id: 'individual', label: '✉️ Send to Individual' }].map(function(tab) {
          var active = activeTab === tab.id;
          return (
            <button key={tab.id} onClick={function() { setActiveTab(tab.id); }}
              style={{ padding: '8px 20px', borderRadius: 9, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 700, background: active ? 'linear-gradient(135deg, #ff6b2b, #ffab40)' : 'transparent', color: active ? '#fff' : '#666' }}>
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* INDIVIDUAL TAB */}
      {activeTab === 'individual' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: 20 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            {indFlash && (
              <div style={{ background: indFlash.error ? '#2d0a0a' : '#0d2818', border: '1px solid ' + (indFlash.error ? '#7f1d1d' : '#1a4d2e'), borderRadius: 10, padding: '10px 16px', color: indFlash.error ? '#f87171' : '#34d399', fontSize: 13, fontWeight: 600 }}>
                {indFlash.text}
              </div>
            )}

            {/* Search */}
            <div style={{ background: '#0d0d1f', borderRadius: 14, padding: 24, border: '1px solid #1e1e35' }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: '#888', marginBottom: 8 }}>Search Customers</label>
              <div style={{ position: 'relative' }}>
                <input value={indSearch} onChange={function(e) { setIndSearch(e.target.value); }} placeholder="Search by name or phone..."
                  style={{ width: '100%', padding: '12px 14px', background: '#12121f', border: '1px solid #2a2a4a', borderRadius: 10, color: '#fff', fontSize: 14, outline: 'none', boxSizing: 'border-box' }} />
                {indSearchLoading && <div style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', color: '#555', fontSize: 12 }}>searching...</div>}
                {indSearchResults.length > 0 && (
                  <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#12121f', border: '1px solid #2a2a4a', borderRadius: 10, marginTop: 4, zIndex: 100, maxHeight: 240, overflowY: 'auto' }}>
                    {indSearchResults.map(function(c) {
                      var alreadyAdded = !!indSelected.find(function(s) { return s.id === c.id; });
                      var displayName = ((c.first_name || '') + ' ' + (c.last_name || '')).trim() || c.name || 'Unknown';
                      return (
                        <div key={c.id} onClick={function() { if (!alreadyAdded) addToSelected(c); }}
                          style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', borderBottom: '1px solid #1e1e35', cursor: alreadyAdded ? 'default' : 'pointer', opacity: alreadyAdded ? 0.4 : 1 }}
                          onMouseEnter={function(e) { if (!alreadyAdded) e.currentTarget.style.background = '#1a1a30'; }}
                          onMouseLeave={function(e) { e.currentTarget.style.background = 'transparent'; }}>
                          <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#ff6b2b20', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: '#ff6b2b', flexShrink: 0 }}>
                            {(displayName[0] || '?').toUpperCase()}
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 14, fontWeight: 600, color: '#fff' }}>{displayName}</div>
                            <div style={{ fontSize: 12, color: '#666' }}>{c.phone}</div>
                          </div>
                          <span style={{ fontSize: 11, color: alreadyAdded ? '#555' : '#ff6b2b', fontWeight: 700 }}>{alreadyAdded ? 'Added' : '+ Add'}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Manual phone */}
            <div style={{ background: '#0d0d1f', borderRadius: 14, padding: 24, border: '1px solid #1e1e35' }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: '#888', marginBottom: 8 }}>Add by Phone Number</label>
              <div style={{ display: 'flex', gap: 8 }}>
                <input value={manualName} onChange={function(e) { setManualName(e.target.value); }} placeholder="Name (optional)"
                  style={{ flex: 1, padding: '11px 14px', background: '#12121f', border: '1px solid #2a2a4a', borderRadius: 10, color: '#fff', fontSize: 14, outline: 'none' }} />
                <input value={manualPhone} onChange={function(e) { setManualPhone(e.target.value); }} placeholder="+17025551234"
                  onKeyDown={function(e) { if (e.key === 'Enter') addManualPhone(); }}
                  style={{ flex: 1, padding: '11px 14px', background: '#12121f', border: '1px solid #2a2a4a', borderRadius: 10, color: '#fff', fontSize: 14, outline: 'none' }} />
                <button onClick={addManualPhone} style={{ padding: '11px 20px', background: '#1e1e35', border: 'none', borderRadius: 10, color: '#ccc', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>+ Add</button>
              </div>
            </div>

            {/* Selected recipients */}
            {indSelected.length > 0 && (
              <div style={{ background: '#0d0d1f', borderRadius: 14, padding: 24, border: '1px solid #1e1e35' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                  <label style={{ fontSize: 13, fontWeight: 700, color: '#888' }}>Recipients ({indSelected.length})</label>
                  <button onClick={function() { setIndSelected([]); }} style={{ background: 'none', border: 'none', color: '#e74c3c', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Clear all</button>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {indSelected.map(function(c) {
                    return (
                      <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', background: '#12121f', borderRadius: 8, border: '1px solid #1e1e35' }}>
                        <div style={{ width: 28, height: 28, borderRadius: '50%', background: '#ff6b2b20', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: '#ff6b2b', flexShrink: 0 }}>
                          {(c.name[0] || '?').toUpperCase()}
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: '#fff' }}>{c.name}</div>
                          <div style={{ fontSize: 11, color: '#666' }}>{c.phone}</div>
                        </div>
                        <button onClick={function() { removeFromSelected(c.id); }} style={{ background: 'none', border: 'none', color: '#e74c3c', fontSize: 16, cursor: 'pointer', padding: '0 4px', lineHeight: 1 }}>✕</button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Message */}
            <div style={{ background: '#0d0d1f', borderRadius: 14, padding: 24, border: '1px solid #1e1e35' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <label style={{ fontSize: 13, fontWeight: 700, color: '#888' }}>Message</label>
                <span style={{ fontSize: 12, fontWeight: 600, color: indMessage.length > 160 ? '#e74c3c' : indMessage.length > 140 ? '#ffab40' : '#555' }}>{indMessage.length}/160</span>
              </div>
              <textarea value={indMessage} onChange={function(e) { setIndMessage(e.target.value); }} rows={4} placeholder="Type your message... Use {first_name} for personalization"
                style={{ width: '100%', padding: '12px 14px', background: '#12121f', border: '1px solid #2a2a4a', borderRadius: 10, color: '#fff', fontSize: 14, outline: 'none', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit', lineHeight: 1.5 }} />
              <div style={{ marginTop: 12, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 11, color: '#555', lineHeight: '24px' }}>Templates:</span>
                {TEMPLATES.map(function(t, i) {
                  return <button key={i} onClick={function() { setIndMessage(t.msg); }} style={{ padding: '4px 10px', background: '#1a1a30', border: '1px solid #2a2a4a', borderRadius: 6, color: '#888', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>{t.name}</button>;
                })}
              </div>
              <div style={{ marginTop: 8, display: 'flex', gap: 6 }}>
                <span style={{ fontSize: 11, color: '#555', lineHeight: '24px' }}>Insert:</span>
                {['{first_name}', '{last_name}'].map(function(v) {
                  return <button key={v} onClick={function() { setIndMessage(indMessage + ' ' + v); }} style={{ padding: '4px 8px', background: '#ff6b2b10', border: '1px solid #ff6b2b30', borderRadius: 6, color: '#ff6b2b', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>{v}</button>;
                })}
              </div>
            </div>

            <button onClick={sendIndividual} disabled={indSending || indSelected.length === 0 || !indMessage.trim()}
              style={{ padding: '16px 32px', background: 'linear-gradient(135deg, #ff6b2b, #ffab40)', color: '#fff', border: 'none', borderRadius: 12, fontSize: 15, fontWeight: 700,
                cursor: (indSending || indSelected.length === 0 || !indMessage.trim()) ? 'not-allowed' : 'pointer',
                opacity: (indSending || indSelected.length === 0 || !indMessage.trim()) ? 0.5 : 1 }}>
              {indSending ? 'Sending...' : indSelected.length === 0 ? 'Add recipients to send' : 'Send to ' + indSelected.length + ' Recipient' + (indSelected.length > 1 ? 's' : '') + ' →'}
            </button>
          </div>

          {/* Preview */}
          <div>
            <div style={{ background: '#0d0d1f', borderRadius: 14, padding: 24, border: '1px solid #1e1e35', position: 'sticky', top: 20 }}>
              <h3 style={{ fontSize: 14, fontWeight: 700, color: '#888', marginBottom: 16, textTransform: 'uppercase', letterSpacing: 0.5 }}>📱 Preview</h3>
              <div style={{ background: '#1a1a30', borderRadius: 16, padding: 16, minHeight: 120 }}>
                <div style={{ background: '#2a2a4a', borderRadius: 12, padding: '12px 14px', maxWidth: '85%', fontSize: 14, color: '#e2e2e8', lineHeight: 1.5, wordBreak: 'break-word' }}>
                  {indMessage
                    ? indMessage.replace(/\{first_name\}/gi, indSelected[0]?.name?.split(' ')[0] || 'Sarah').replace(/\{last_name\}/gi, 'Johnson')
                    : 'Your message preview will appear here...'}
                </div>
                {indMessage && <div style={{ marginTop: 8, fontSize: 11, color: '#555' }}>{indMessage.length <= 160 ? '1 segment' : Math.ceil(indMessage.length / 153) + ' segments'} · {indMessage.length} chars</div>}
              </div>
              {indSelected.length > 0 && (
                <div style={{ marginTop: 16, padding: '12px 14px', background: '#12121f', borderRadius: 10, border: '1px solid #1e1e35' }}>
                  <div style={{ fontSize: 12, color: '#888', marginBottom: 6 }}>Sending to:</div>
                  {indSelected.slice(0, 5).map(function(c) {
                    return <div key={c.id} style={{ fontSize: 13, color: '#ccc', marginBottom: 2 }}>• {c.name} ({c.phone})</div>;
                  })}
                  {indSelected.length > 5 && <div style={{ fontSize: 12, color: '#555', marginTop: 4 }}>+{indSelected.length - 5} more</div>}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* CAMPAIGNS TAB */}
      {activeTab === 'campaigns' && (
        <div>
          {campaigns.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 20px' }}>
              <div style={{ fontSize: 56, marginBottom: 16 }}>📱</div>
              <h2 style={{ fontSize: 20, fontWeight: 700, color: '#fff', marginBottom: 8 }}>No campaigns yet</h2>
              <p style={{ color: '#888', fontSize: 14, marginBottom: 24 }}>Create your first SMS campaign to reach your customers.</p>
              <button onClick={function() { openCompose(null); }} style={{ padding: '14px 32px', background: 'linear-gradient(135deg, #ff6b2b, #ffab40)', color: '#fff', border: 'none', borderRadius: 12, fontSize: 15, fontWeight: 700, cursor: 'pointer' }}>
                Create Campaign
              </button>
            </div>
          ) : (
            <div>
              {drafts.length > 0 && (
                <div style={{ marginBottom: 32 }}>
                  <h2 style={{ fontSize: 16, fontWeight: 700, color: '#888', marginBottom: 12 }}>Drafts & Scheduled</h2>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {drafts.map(function(c) {
                      return (
                        <div key={c.id} style={{ background: '#0d0d1f', borderRadius: 14, padding: '20px 24px', border: '1px solid #1e1e35', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
                          <div style={{ flex: 1, minWidth: 200 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                              <span style={{ fontSize: 16, fontWeight: 700, color: '#fff' }}>{c.name}</span>
                              <span style={statusBadge(c.status)}>{c.status}</span>
                            </div>
                            <div style={{ fontSize: 13, color: '#666', marginTop: 4 }}>{c.audience_count} recipients · "{c.message?.slice(0, 60)}{c.message?.length > 60 ? '...' : ''}"</div>
                          </div>
                          <div style={{ display: 'flex', gap: 8 }}>
                            <button onClick={function() { openCompose(c); }} style={{ padding: '8px 18px', background: '#1e1e35', border: 'none', borderRadius: 8, color: '#ccc', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Edit</button>
                            <button onClick={function() { sendCampaign(c.id); }} disabled={sending} style={{ padding: '8px 18px', background: 'linear-gradient(135deg, #ff6b2b, #ffab40)', border: 'none', borderRadius: 8, color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>Send</button>
                            <button onClick={function() { deleteCampaign(c.id); }} style={{ padding: '8px 14px', background: '#1e1e35', border: 'none', borderRadius: 8, color: '#e74c3c', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>✕</button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              {sent.length > 0 && (
                <div>
                  <h2 style={{ fontSize: 16, fontWeight: 700, color: '#888', marginBottom: 12 }}>Sent Campaigns</h2>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {sent.map(function(c) {
                      return (
                        <div key={c.id} style={{ background: '#0d0d1f', borderRadius: 14, padding: '20px 24px', border: '1px solid #1e1e35' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
                            <div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                                <span style={{ fontSize: 16, fontWeight: 700, color: '#fff' }}>{c.name}</span>
                                <span style={statusBadge(c.status)}>{c.status}</span>
                              </div>
                              <div style={{ fontSize: 13, color: '#666', marginTop: 4 }}>"{c.message?.slice(0, 80)}{c.message?.length > 80 ? '...' : ''}"</div>
                            </div>
                            <div style={{ display: 'flex', gap: 20, fontSize: 13 }}>
                              <div style={{ textAlign: 'center' }}><div style={{ fontWeight: 800, color: '#00b894', fontSize: 18 }}>{c.sent_count || 0}</div><div style={{ color: '#666' }}>Sent</div></div>
                              <div style={{ textAlign: 'center' }}><div style={{ fontWeight: 800, color: '#e74c3c', fontSize: 18 }}>{c.failed_count || 0}</div><div style={{ color: '#666' }}>Failed</div></div>
                              <div style={{ textAlign: 'center' }}><div style={{ fontWeight: 800, color: '#888', fontSize: 18 }}>{c.audience_count || 0}</div><div style={{ color: '#666' }}>Total</div></div>
                            </div>
                          </div>
                          {c.sent_at && <div style={{ fontSize: 12, color: '#555', marginTop: 8 }}>Sent {new Date(c.sent_at).toLocaleString()}</div>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default SMSCampaigns;
